import { useCallback, useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import AndroidIcon from '@mui/icons-material/Android';
import { QRCodeSVG } from 'qrcode.react';

import * as releasesApi from '../api/captureAppReleases';
import type { CaptureAppInstaller } from '../api/captureAppReleases';
import { extractErrorMessage } from '../api/errors';

/**
 * Install-capture-app QR panel on the tenant Devices page.
 *
 * Collapsed by default — tenant_admins scan it once per device, so
 * the full QR doesn't need to live on the page permanently. Tapping
 * "Show install QR" requests a fresh presigned URL from the API and
 * renders it as a scannable QR for an Android phone's browser.
 *
 * The presigned URL is short-lived (5 min server-side); we re-fetch
 * every ~4 minutes while the panel is open so a staff member who
 * leaves the page open doesn't end up with a dead QR. Closing the
 * panel cancels the refresh cycle.
 *
 * Sister-surface to the existing enrollment QR — the operator
 * onboarding flow becomes: scan THIS QR to install, then open the
 * app and scan the enrollment QR below to register the device.
 */

const REFRESH_AFTER_MS = 4 * 60 * 1000;  // 4 min — under the server's 5 min TTL

export function InstallCaptureAppPanel({ tenantId }: { tenantId: string }) {
  const [open, setOpen] = useState(false);
  const [installer, setInstaller] = useState<CaptureAppInstaller | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchInstaller = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setInstaller(await releasesApi.getCaptureAppInstaller(tenantId));
    } catch (err) {
      setError(extractErrorMessage(err, 'failed to load installer'));
      setInstaller(null);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  // Fetch on open + every REFRESH_AFTER_MS while open so the QR
  // doesn't go stale on a long-lived page.
  useEffect(() => {
    if (!open) return;
    void fetchInstaller();
    const id = window.setInterval(() => void fetchInstaller(), REFRESH_AFTER_MS);
    return () => window.clearInterval(id);
  }, [open, fetchInstaller]);

  const shaShort = installer?.sha256?.slice(0, 12) ?? null;

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <AndroidIcon color="action" />
            <Stack>
              <Typography variant="subtitle1" fontWeight={600}>
                Install capture app
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Scan-to-install QR for staff Android devices. Pairs with
                the enrollment QR below — install first, then enroll.
              </Typography>
            </Stack>
          </Stack>
          <Button
            variant={open ? 'outlined' : 'contained'}
            onClick={() => setOpen(!open)}
            size="small"
          >
            {open ? 'Hide' : 'Show install QR'}
          </Button>
        </Stack>

        <Collapse in={open}>
          <Divider sx={{ my: 2 }} />
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {loading && !installer && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={20} />
            </Box>
          )}
          {installer && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={3}
              alignItems="center"
            >
              {/* Always white with a quiet zone — on dark paper, with the
                  library's default zero margin, phone cameras don't even
                  recognise the code. */}
              <Box
                sx={{
                  bgcolor: 'common.white',
                  lineHeight: 0,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 1.5,
                }}
              >
                <QRCodeSVG
                  value={installer.qr_url}
                  size={240}
                  level="M"
                  marginSize={4}
                  bgColor="#ffffff"
                  fgColor="#000000"
                  aria-label="Install QR"
                />
              </Box>
              <Stack spacing={1} sx={{ flex: 1 }}>
                <Stack direction="row" spacing={2}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Version
                    </Typography>
                    <Typography variant="body2" fontFamily="monospace">
                      {installer.version}
                    </Typography>
                  </Box>
                  {shaShort && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        SHA-256
                      </Typography>
                      <Typography variant="body2" fontFamily="monospace">
                        {shaShort}…
                      </Typography>
                    </Box>
                  )}
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  On the target Android device, open the camera or any
                  QR scanner and point it at this code. The browser will
                  download the APK; tap the notification to install.
                  First-time installs need <strong>Install unknown apps</strong>
                  {' '}allowed for the browser in Settings.
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  This QR auto-refreshes — leave the panel open while
                  onboarding multiple devices.
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  href={installer.url}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Download APK directly
                </Button>
              </Stack>
            </Stack>
          )}
        </Collapse>
      </CardContent>
    </Card>
  );
}
